import type { Channel } from '@core/channel'
import type { FeedSourceConfig } from '@core/feed'
import { FeedType } from '@core/feed'
import { Context } from '@embed/components/helpers'

import type { IContextTrackingData, IContextTrackingDataPixel } from './types'

/**
 * Helper function to build trackingData payload out
 * of widget params and fetched channel/publisherClient
 */
export const mapConfigToTrackingData = (
  config?: FeedSourceConfig | null,
  channel?: Channel | null,
): {
  trackingData: IContextTrackingData
  trackingDataPixel: IContextTrackingDataPixel
} | null => {
  if (!config) {
    return null
  }
  switch (true) {
    case config.type === FeedType.PLAYLIST: {
      return {
        trackingData: {
          context: Context.CONTEXT_PLAYLIST,
          channel: channel?.username,
          channel_id: channel?.encoded_id,
          playlist_id: config.playlistId,
        },
        trackingDataPixel: {
          context: Context.CONTEXT_PLAYLIST,
          channel: channel?.username,
          _channel_id: channel?.encoded_id,
          _playlist_id: config.playlistId,
        },
      }
    }
    case config.type === FeedType.PLAYLIST_COLLECTION: {
      return {
        trackingData: {
          context: Context.CONTEXT_PLAYLIST_COLLECTION,
          channel: channel?.username,
          channel_id: channel?.encoded_id,
          playlist_id: config.playlistId,
        },
        trackingDataPixel: {
          context: Context.CONTEXT_PLAYLIST_COLLECTION,
          channel: channel?.username,
          _channel_id: channel?.encoded_id,
          _playlist_id: config.playlistId,
        },
      }
    }
    case config.type === FeedType.PRODUCT_SKU: {
      return {
        trackingData: {
          context: Context.CONTEXT_SKUS,
          channel: channel?.username,
          channel_id: channel?.encoded_id,
          skus: config.skus,
        },
        trackingDataPixel: {
          context: Context.CONTEXT_SKUS,
          channel: channel?.username,
          _channel_id: channel?.encoded_id,
          skus: config.skus,
        },
      }
    }
    case config.type === FeedType.USER_TIMELINE_HASHTAG: {
      return {
        trackingData: {
          context: Context.CONTEXT_HASHTAG,
          hashtag: config.hashtagExpression,
          channel: channel?.username,
          channel_id: channel?.encoded_id,
        },
        trackingDataPixel: {
          context: Context.CONTEXT_HASHTAG,
          hashtag: config.hashtagExpression,
          channel: channel?.username,
          _channel_id: channel?.encoded_id,
        },
      }
    }
    case config.type === FeedType.USER_TIMELINE: {
      return {
        trackingData: {
          context: Context.CONTEXT_CHANNEL,
          channel: channel?.username,
          channel_id: channel?.encoded_id,
        },
        trackingDataPixel: {
          context: Context.CONTEXT_CHANNEL,
          channel: channel?.username,
          _channel_id: channel?.encoded_id,
        },
      }
    }
    case config.type === FeedType.USER_VIDEOS: {
      return {
        trackingData: {
          context: Context.CONTEXT_VIDEOS,
          channel: channel?.username,
          channel_id: channel?.encoded_id,
          videos: config.videoIds?.join(','),
        },
        trackingDataPixel: {
          context: Context.CONTEXT_VIDEOS,
          channel: channel?.username,
          _channel_id: channel?.encoded_id,
          _videos: config.videoIds?.join(','),
        },
      }
    }
    default: {
      return null
    }
  }
}
